import { Component, OnInit, OnDestroy } from "@angular/core";
import { WebCustomer, AdvancedSearchOptions } from "src/app/models";
import { SearchFilterDialogComponent } from "./search-filter-dialog/search-filter-dialog.component";
import { Subscription } from "rxjs";
import { OldCustomerSearchService } from "src/app/services/old-customer-search.service";
import { NewCustomerSearchService } from "src/app/services/new-customer-search.service";
import { CompanyService, SharedService, CustomerSearchOptionsService } from "src/app/services";
import { CustomerService } from "src/app/services/customer.service";
import { SpinnerService } from "src/app/services/spinner.service";
import { Router } from "@angular/router";
import { MatDialog } from "@angular/material/dialog";
import { ChangeDetectorRef } from "@angular/core";
import { take } from "rxjs/operators";

@Component({
  selector: "app-customer-search",
  templateUrl: "./customer-search.component.html",
  styleUrls: ["./customer-search.component.scss"]
})
export class CustomerSearchComponent implements OnInit, OnDestroy
{
  title: string = "Customer Search";
  searchText: string = "";
  customerSearchItems: WebCustomer[] = [];
  searchOptions: AdvancedSearchOptions;
  areSearchOptionsValid: boolean;
  isDefaultSearch: boolean;
  oldCustomerSearchSubscription: Subscription;
  newCustomerSearchSubscription: Subscription;
  isSearchButtonDisabled: boolean;
  isSubmitted: boolean;
  noSearchResultsMessage: string = "";
  isSearching: boolean;
  isNewSearchAvailable: boolean;

  constructor(
    private oldCustomerSearchService: OldCustomerSearchService,
    private newCustomerSearchService: NewCustomerSearchService,
    private customerService: CustomerService,
    public spinnerService: SpinnerService,
    public companyService: CompanyService,
    public dialog: MatDialog,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private searchOptionsService: CustomerSearchOptionsService,
  ) { }

  ngOnInit(): void
  {
    this.companyService.isNewSearchAvailable$
      .pipe(take(1))
      .subscribe(isNewSearchAvailable =>
      {
        this.isNewSearchAvailable = isNewSearchAvailable;
        if (isNewSearchAvailable)
        {
          this.checkSearchOptions();

          this.newCustomerSearchSubscription = this.newCustomerSearchService.customerList
            .subscribe(customers =>
            {
              if (!this.isSearching) return;

              this.customerSearchItems = customers;
              this.setAcctStatColor();
              this.checkForNoSearchResults(this.customerSearchItems);
              this.isSubmitted = false;
              this.isSearchButtonDisabled = false;
            });

        } 
        else
        {
          this.oldCustomerSearchSubscription = this.oldCustomerSearchService.customerList
            .subscribe(customers =>
            {
              if (!this.isSearching) return;

              this.customerSearchItems = customers;
              this.setAcctStatColor();
              this.checkForNoSearchResults(this.customerSearchItems);
              this.isSubmitted = false;
              this.isSearchButtonDisabled = false;
            });
        }
      });
  }

  setAcctStatColor(): void
  {
    this.customerSearchItems.forEach(c =>
    {
      if (c.AcctStat === 6 || c.AcctStat === 3)
      {
        c.AcctStatColor = "#FF0000";
      }
    });
  }

  onSearchPressed(searchText: string): void
  {
    this.noSearchResultsMessage = '';
    this.isSearchButtonDisabled = true;
    this.isSubmitted = true;
    this.isSearching = true;
    this.cdr.detectChanges();

    if (this.isNewSearchAvailable)
    {
      if (SharedService.IsNullOrEmpty(this.searchOptions))
      {
        this.searchOptions = JSON.parse(localStorage.getItem("searchOptions"))
      }
      if (this.searchOptionsService.validateSearchOptions(this.searchOptions))
      {
        this.areSearchOptionsValid = true;
        this.searchOptions.SearchFor = searchText;
        this.newCustomerSearchService.getCustomerListItemsAdvanced(this.searchOptions);
        
      } 
      else
      {
        this.openSearchFilter();
      }
    } 
    else
    {
      this.oldCustomerSearchService.getCustomerListItems(searchText);
    }
  }

  checkForNoSearchResults(searchResults: WebCustomer[]): void
  {
    if (searchResults.length === 0)
    {
      this.noSearchResultsMessage = "No search results found. Please adjust your search and try again.";
    } 
    else
    {
      this.noSearchResultsMessage = "";
    }
    this.isSearching = false;
  }

  selectCustomer(selectedCustomer: WebCustomer): void
  {
    this.customerService.selectCustomer(selectedCustomer);
    this.router.navigate(["home"]);
  }

  clearSearchResults(): void
  {
    this.searchText = '';
    this.customerSearchItems = [];
    this.noSearchResultsMessage = "";
    this.isSubmitted = false;
    this.isSearchButtonDisabled = false;
  }

  checkSearchOptions(): void
  {
    this.searchOptions = JSON.parse(localStorage.getItem('searchOptions'));
    if (SharedService.IsNullOrEmpty(this.searchOptions))
    {
      const defaultSearchOptions = this.searchOptionsService.setCustomerSearchOptionsToDefault();
      this.searchOptions = defaultSearchOptions;
      localStorage.setItem('searchOptions', JSON.stringify(defaultSearchOptions));
    }
    this.areSearchOptionsValid = this.searchOptionsService.validateSearchOptions(this.searchOptions);
    this.isDefaultSearch = this.searchOptionsService.checkIfDefaultCustomerSearch(this.searchOptions);
    if (!this.areSearchOptionsValid)
    {
      this.openSearchFilter();
      this.searchOptionsService.showAlertDialog();
    }
  }

  openSearchFilter(): void
  {
    let savedSearchOptions = JSON.parse(localStorage.getItem('searchOptions'));
    const areSearchOptionsValid = this.areSearchOptionsValid
    if (SharedService.IsNullOrEmpty(savedSearchOptions))
    {
      this.areSearchOptionsValid = false;
      savedSearchOptions = {};
    }

    const dialogRef = this.dialog.open(SearchFilterDialogComponent, {
      minWidth: 300,
      maxWidth: "800px",
      maxHeight: "800px",
      disableClose: true,
      data: { savedSearchOptions, areSearchOptionsValid },
      panelClass: 'search-filter-dialog'
    });

    dialogRef.componentInstance.applyClicked.subscribe((options: AdvancedSearchOptions) =>
    {
      this.searchOptions = options;
      this.areSearchOptionsValid = this.searchOptionsService.validateSearchOptions(this.searchOptions);
      this.isDefaultSearch = this.searchOptionsService.checkIfDefaultCustomerSearch(this.searchOptions);
    });
  }

  ngOnDestroy(): void
  {
    if (this.newCustomerSearchSubscription)
    {
      this.newCustomerSearchSubscription.unsubscribe();
    } 
    else
    {
      this.oldCustomerSearchSubscription.unsubscribe();
    }
  }
}